import * as Yup from 'yup';
import { ID, IModel } from '~/lib/model';
import { AccountCondition } from '~/modules/Clients/models/AccountInfo';

export enum AgencyPaymentType {
    INTERNAL = 'internal',
    EXTERNAL = 'external'
}

export const dspCountingTypes = ['BURL', 'NURL', 'ADM', 'BURL+ADM', 'NURL+ADM'];

export const agencyPaymentTypes: string[] = Object.keys(AgencyPaymentType).map(
  k => AgencyPaymentType[k as keyof typeof AgencyPaymentType]
);

export interface IAdSystemDomain {
  id?: number;
  domain: string;
  tagId?: string;
  default: boolean;
}

export interface IAgencyShort extends IModel {
  title: string;
  externalId?: number;
  isActive: boolean;
  dspConnectionType?: string;
  p2pCoefficient?: number;
  accountManagerId?: number;
}

export enum AgencyNetwork {
  APPODEAL = 'appodeal',
}

export const agencyNetworkLabels: Record<AgencyNetwork, string> = {
  [AgencyNetwork.APPODEAL]: 'Appodeal',
};

const REPORTING_TIMEZONE_FALLBACK = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Kyiv',
  'Asia/Jerusalem',
  'Asia/Kolkata',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Australia/Sydney',
];

const supportedTimezones = (): string[] => {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
  return intl.supportedValuesOf ? intl.supportedValuesOf('timeZone') : REPORTING_TIMEZONE_FALLBACK;
};

export const reportingTimezoneOptions: { label: string; value: string }[] = supportedTimezones().map(
  (tz) => ({ label: tz, value: tz })
);

export interface IAgency extends IModel {
  title: string;
  externalId?: number;
  active?: boolean;
  contactName?: string;
  instantMessaging?: string;
  phone?: string;
  email?: string;
  site?: string;
  paymentType: string;
  netTerms?: AccountCondition;
  dspConnectionType?: string;
  p2pCoefficient?: number;
  p2pPaasFee?: number | null;
  p2pGrossBidPrice: boolean;
  dataCubes: ID[];
  accountManagerId: number | null;
  dspCountingType: string;
  adSystemDomains: IAdSystemDomain[];
  network?: AgencyNetwork | null;
  reportingTimezone?: string | null;
  attributes?: IAgencyAttributes | null;
  presetId?: number | null;
}

/**
 * An agency with a DSP connection type runs on the P2P fee path in the auction; one without it is an open-market DSP
 * integration.
 */
export const isP2pAgency = (dspConnectionType?: string | null): boolean => !!dspConnectionType?.trim();

export const REPORTING_API_URL_HELP =
  "Several accounts: comma-separated, in the same order as the tokens. No dates and no keys in the URL.";

export interface IAgencyAttributes {
  p2pPaas?: boolean;
  p2pReversedPayment?: boolean;
  reportingApiUrl?: string | null;
  accessToken?: string | null;
  accessUsername?: string | null;
  accessPassword?: string | null;
  publisherId?: string | null;
}

export interface IAgenciesFilter {
  id?: string;
  title?: string;
  active?: boolean;
  dspConnectionType?: string[];
  dspConnectionTypeExclude?: boolean;
  accountManagerId?: number;
}

export const AgencySchema = Yup.object().shape<IAgency>({
  id: null,
  title: Yup.string().required().nullable(false),
  paymentType: Yup.string().required().nullable(false),
  netTerms: Yup.mixed<AccountCondition>()
    .oneOf([...Object.values(AccountCondition), null, undefined])
    .when('id', {
      is: (id: ID) => id == null,
      then: Yup.mixed<AccountCondition>().oneOf(Object.values(AccountCondition)).required(),
      otherwise: Yup.mixed<AccountCondition>().nullable(),
    }),
  dspConnectionType: Yup.string().nullable(true),
  p2pCoefficient: Yup.number()
    .moreThan(0)
    .lessThan(100)
    .nullable(),
  p2pPaasFee: Yup.number()
    .min(0)
    .max(10)
    .test('max-2-decimals', 'P2P PaaS Fee % must have at most 2 decimal places', v => {
      if (v == null) return true;
      const decimals = v.toString().split('.')[1];
      return !decimals || decimals.length <= 2;
    })
    .nullable(),
  accountManagerId: Yup.number().nullable(),
  network: Yup.string().nullable(),
  reportingTimezone: Yup.string().nullable(),
  attributes: Yup.object().shape<IAgencyAttributes>({
    p2pPaas: Yup.boolean().nullable(),
    p2pReversedPayment: Yup.boolean().when('p2pPaas', {
      is: true,
      then: Yup.boolean().oneOf([false], 'P2P PaaS and reversed payments cannot both be enabled'),
    }),
    reportingApiUrl: Yup.string().nullable(),
    accessToken: Yup.string().nullable(),
    accessUsername: Yup.string().nullable(),
    accessPassword: Yup.string().nullable(),
    publisherId: Yup.string().nullable(),
  }).nullable(),
  dataCubes: Yup.array().of(Yup.number().nullable()),
  presetId: Yup.number().nullable().required('Preset is required'),
adSystemDomains: Yup.array()
  .of(
    Yup.object().shape<IAdSystemDomain>({
      id: Yup.number().nullable(),
      domain: Yup.string().required("Domain is required").nullable(false),
      tagId: Yup.string().nullable(),
      default: Yup.boolean().required(),
    })
  )
  .min(1, "At least one domain is required")
  .required(),

});

export const defaultAgency: IAgency = {
  id: null,
  title: '',
  paymentType: 'internal',
  accountManagerId: null,
  dspCountingType: 'BURL',
  p2pGrossBidPrice: true,
  adSystemDomains: [],
  dataCubes: [],
  presetId: null,
};

export const onSubmitPreHandle = (agency: IAgency): IAgency => {
    if (typeof agency.p2pCoefficient === 'string' && (agency.p2pCoefficient as string) === '') {
        return {
            ...agency,
            p2pCoefficient: null
        };
    }
    return agency;
};

export type DataCubeAgency = {
    id: ID;
    name: string;
}
